import { useEffect, useState } from "react";
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { usePlan, useCheckout, useRedeem, isPlanActive } from "../../app/hooks/useBilling.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { planDate } from "./planFormat.js";
import { canPurchaseHere } from "../../app/inAppPurchase.js";
import styles from "./ActivateScreen.module.scss";

/** The go-live cadences (identifiers seeded in core.plans). Prices are SERVER data (checkout reads core.plans) —
 * a full pricing display is a Phase-4 refinement (GATE-6 is designed here, fully exercised at Phase 4). */
const CADENCES: ReadonlyArray<{ key: string; label: string; note: string }> = [
  { key: "artist_cntrl_monthly", label: "Monthly", note: "Billed monthly" },
  { key: "artist_cntrl_annual", label: "Annual", note: "Billed once a year" },
];

/**
 * The subscription paywall (P-08). A band with an inactive plan is
 * routed here PRE-PAINT (the dashboard guard) and cannot reach the app until the plan is active — the revenue
 * gate. Checkout leads into Stripe (full-page nav, never a webview; never optimistic — the WEBHOOK activates),
 * a promo code can comp the plan, and "Back to login" logs out. The screen is a pure component (band + callbacks
 * as props) so it renders outside the band shell and tests in isolation.
 */
export function ActivateScreen({
  bandId,
  bandName,
  checkoutReturn,
  onActivated,
  onBackToLogin,
  onCheckoutUrl = (url: string) => window.location.assign(url),
}: {
  bandId: string | undefined;
  /** The band's name, for the crew's "{Band}'s plan isn't active" (ART-350). */
  bandName?: string;
  checkoutReturn?: "done" | "canceled";
  onActivated: () => void;
  onBackToLogin: () => void;
  onCheckoutUrl?: (url: string) => void;
}) {
  const plan = usePlan(bandId);
  const checkout = useCheckout(bandId);
  const redeem = useRedeem(bandId);
  const [code, setCode] = useState("");

  // Stripe return: a completed checkout activates via the async webhook shortly after — refetch to catch it.
  useEffect(() => {
    if (checkoutReturn === "done") void plan.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per return, not on every plan-query identity change
  }, [checkoutReturn]);

  if (!bandId) {
    return (
      <main className={styles.gate}>
        <EmptyState title="Nothing to activate" description="This account has no artist to subscribe. Try signing in again." />
      </main>
    );
  }

  if (plan.isLoading) {
    return (
      <main className={styles.gate}>
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner size={24} label="Checking your subscription…" />
        </div>
      </main>
    );
  }

  if (plan.isError) {
    return (
      <main className={styles.gate}>
        <EmptyState
          title="Couldn't load your subscription"
          description={plan.error instanceof ApiError ? plan.error.message : "Please try again."}
          action={<Button onClick={() => void plan.refetch()}>Retry</Button>}
        />
      </main>
    );
  }

  // The 14-day free trial (ART-348). A band still on it comes here from Settings › Choose plan, to pick the plan
  // it will pay for once the trial ends; one whose trial is over lands here from the paywall, and is told why.
  const onTrial = plan.data?.billedVia === "trial";
  const trialEnds = planDate(plan.data?.trialEndsOn);

  // Already active (e.g. a webhook landed while the return page was open) — offer to continue into the app.
  if (isPlanActive(plan.data?.status) && !onTrial) {
    return (
      <main className={styles.gate}>
        <div className={styles.card}>
          <h1 className={styles.title}>You're all set</h1>
          <p className={styles.blurb}>Your subscription is active.</p>
          <Button onClick={onActivated}>Continue</Button>
        </div>
      </main>
    );
  }

  // The expired wording (ART-350, PO 2026-10-29), one place for the web and the app: a trial that ran out reads as
  // the trial, anything else as the subscription.
  const expiredTitle = trialEnds ? "Your trial period has expired" : "Your subscription has expired";
  const expiredLine = "Renew to continue using Artist CNTRL.";

  // CREW — a band seat that cannot choose a plan (the served `action` is "none" for it): no buttons, just who can
  // fix it (ART-350). Never the plan choice, whose every button would be refused for this seat.
  if (!onTrial && plan.data?.action === "none") {
    return (
      <main className={styles.gate}>
        <div className={styles.card}>
          <h1 className={styles.title}>{bandName ? `${bandName}\u2019s plan isn\u2019t active` : "This band\u2019s plan isn\u2019t active"}</h1>
          <p className={styles.blurb}>The band&rsquo;s owner can restart it.</p>
          <button type="button" className={styles.backLink} onClick={onBackToLogin}>Back to login</button>
        </div>
      </main>
    );
  }

  // Inside the app no purchase is offered (ART-346): the screen says the plan has expired, with no button, link,
  // price or promo code. Logging out is the one way on.
  if (!canPurchaseHere()) {
    return (
      <main className={styles.gate}>
        <div className={styles.card}>
          {onTrial ? (
            <>
              <h1 className={styles.title}>You&rsquo;re on your free trial</h1>
              <p className={styles.blurb}>{trialEnds ? `It ends ${trialEnds}.` : "It starts when you first sign in."}</p>
            </>
          ) : (
            <>
              <h1 className={styles.title}>{expiredTitle}</h1>
              <p className={styles.blurb}>{expiredLine}</p>
            </>
          )}
          {onTrial ? (
            <button type="button" className={styles.backLink} onClick={onActivated}>Back to the app</button>
          ) : (
            <button type="button" className={styles.backLink} onClick={onBackToLogin}>Back to login</button>
          )}
        </div>
      </main>
    );
  }

  // The plan the band picked on its /apply application, offered first (929). Only when it is still on sale.
  const applied = plan.data?.appliedPlanKey
    ? plan.data.options?.find((o) => o.planKey === plan.data?.appliedPlanKey)
    : undefined;

  return (
    <main className={styles.gate}>
      <div className={styles.card}>
        {onTrial ? (
          <>
            <h1 className={styles.title}>Choose your plan</h1>
            <p className={styles.blurb}>
              {trialEnds ? `Your free trial ends ${trialEnds}. ` : ""}Pick a plan now and you won&rsquo;t be charged
              until the trial is over.
            </p>
          </>
        ) : (
          <>
            <h1 className={styles.title}>{expiredTitle}</h1>
            <p className={styles.blurb}>{expiredLine}</p>
          </>
        )}

        {checkoutReturn === "canceled" && (
          <p className={styles.notice} role="status">Checkout was canceled — you can try again below.</p>
        )}

        <div className={styles.plans}>
          {applied && (
            <button
              type="button"
              className={styles.plan}
              disabled={checkout.isPending}
              onClick={() => checkout.mutate(applied.planKey!, { onSuccess: (url) => onCheckoutUrl(url) })}
            >
              <span className={styles.planLabel}>{applied.name}</span>
              <span className={styles.planNote}>
                {priceLine(applied.amountCents ?? 0, applied.interval ?? "")} · the plan you applied with
              </span>
            </button>
          )}
          {CADENCES.filter((c) => c.key !== applied?.planKey).map((c) => (
            <button
              key={c.key}
              type="button"
              className={styles.plan}
              disabled={checkout.isPending}
              onClick={() => checkout.mutate(c.key, { onSuccess: (url) => onCheckoutUrl(url) })}
            >
              <span className={styles.planLabel}>{c.label}</span>
              <span className={styles.planNote}>{c.note}</span>
            </button>
          ))}
        </div>

        {checkout.isError && (
          <p className={styles.error} role="alert">
            {checkout.error instanceof ApiError ? checkout.error.message : "Checkout is temporarily unavailable. Please try again."}
          </p>
        )}

        <form
          className={styles.promo}
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = code.trim();
            if (!trimmed) return;
            redeem.mutate(trimmed, {
              onSuccess: (r) => {
                if (isPlanActive(r.status)) onActivated();
                else void plan.refetch();
              },
            });
          }}
        >
          <label className={styles.promoLabel} htmlFor="promo-code">Have a promo code?</label>
          <div className={styles.promoRow}>
            <input
              id="promo-code"
              className={styles.promoInput}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code"
              autoComplete="off"
            />
            <Button type="submit" variant="ghost" disabled={redeem.isPending || code.trim().length === 0}>
              Redeem
            </Button>
          </div>
          {redeem.isError && (
            <p className={styles.error} role="alert">
              {redeem.error instanceof ApiError ? redeem.error.message : "That code could not be applied."}
            </p>
          )}
        </form>

        {onTrial ? (
          <button type="button" className={styles.backLink} onClick={onActivated}>Back to the app</button>
        ) : (
          <button type="button" className={styles.backLink} onClick={onBackToLogin}>Back to login</button>
        )}
      </div>
    </main>
  );
}

/** "$120/mo" or "$1,440/yr", from a plan's own price. */
function priceLine(amountCents: number, interval: string): string {
  const dollars = (amountCents / 100).toLocaleString("en-US", { maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2 });
  return `$${dollars}/${interval === "year" ? "yr" : "mo"}`;
}
